import {
    Event,
    EventMessageConfig,
    EventNotify,
    EventType,
    HasFunId,
    Property,
    Resource,
    ResourceType,
    RwFlag,
    Service
} from "@/model/ThingModel";
import DataSpec, {DataType} from "@/module/thing/DataSpec";
import ParamProxy from "@/module/thing/ParamProxy";
import ObjectUtil from "tools-ts/src/util/ObjectUtil";

export class ResourcesUpsertForm {
    productId!: number
    properties: Array<Property> = []
    events: Array<Event> = []
    services: Array<Service> = []

    constructor(productId: number) {
        this.productId = productId;
    }
}

export class TmodelEditForm {
    productId!: number
    properties: Array<Property> = []
    events: Array<Event> = []
    services: Array<Service> = []

    constructor(productId: number) {
        this.productId = productId;
    }
}

export interface ResourceUpsertForm<R extends Resource> {
    resourceType: ResourceType;
    name: string;
    identifier: string;
    description: string | undefined;

    toResource(): R;
}

abstract class BaseResourceUpsertForm<R extends Resource> implements ResourceUpsertForm<R> {
    resourceType!: ResourceType
    name = ""
    identifier = ""
    description: string | undefined = undefined

    abstract toResource(): R

    protected toBaseResource(resource: R): R {
        resource.identifier = this.identifier
        resource.name = this.name
        resource.description = this.description
        return resource
    }

    protected fromBaseResource(resource: R): void {
        this.identifier = resource.identifier
        this.name = resource.name
        this.description = resource.description
    }
}

export class PropertyUpsertForm extends BaseResourceUpsertForm<Property> {
    readonly resourceType = ResourceType.PROPERTY
    dataType!: DataType
    dataSpecs!: Array<DataSpec>
    rwFlag!: RwFlag
    default: string | undefined = undefined

    toResource(): Property {
        const resource = new Property()
        this.toBaseResource(resource)
        resource.dataType = this.dataType
        resource.dataSpecs = this.dataSpecs
        resource.rwFlag = this.rwFlag
        resource.default = this.default
        return resource
    }
}

export class EventUpsertForm extends BaseResourceUpsertForm<Event> implements HasFunId {
    readonly resourceType = ResourceType.EVENT
    funId!: number
    type!: EventType
    outputParamProxies = Array<ParamProxy>()
    notify: EventNotify = new EventNotify()
    messageConfig: EventMessageConfig = new EventMessageConfig()

    constructor(eventType: EventType) {
        super();
        this.type = eventType;
    }

    toResource(): Event {
        const resource = new Event()
        this.toBaseResource(resource)
        resource.funId = this.funId
        resource.type = this.type
        resource.outputParamProxies = this.outputParamProxies
        resource.notify = ObjectUtil.deepClone(this.notify)
        resource.messageConfig = ObjectUtil.deepClone(this.messageConfig)
        return resource
    }
}

export class ServiceUpsertForm extends BaseResourceUpsertForm<Service> implements HasFunId {
    readonly resourceType = ResourceType.SERVICE
    funId!: number
    inputParamProxies = Array<ParamProxy>()
    outputParamProxies = Array<ParamProxy>()

    toResource(): Service {
        const resource = new Service()
        this.toBaseResource(resource)
        resource.funId = this.funId
        resource.inputParamProxies = this.inputParamProxies
        resource.outputParamProxies = this.outputParamProxies
        return resource
    }
}

export class PropertyCreateForm extends PropertyUpsertForm {
}

export class EventCreateForm extends EventUpsertForm {
}

export class ServiceCreateForm extends ServiceUpsertForm {
}

export class PropertyEditForm extends PropertyUpsertForm {
    constructor(property: Property | undefined = undefined) {
        super();
        if (property == undefined) return
        this.fromBaseResource(property)
        this.dataType = property.dataType
        this.dataSpecs = property.dataSpecs
        this.rwFlag = property.rwFlag
        this.default = property.default
    }
}

export class EventEditForm extends EventUpsertForm {
    constructor(event: Event | undefined = undefined) {
        super(event?.type ?? EventType.INFO);
        if (event == undefined) return
        this.fromBaseResource(event)
        this.funId = event.funId
        this.outputParamProxies = event.outputParamProxies
        this.notify = event.notify
        this.messageConfig = event.messageConfig
    }
}

export class ServiceEditForm extends ServiceUpsertForm {
    constructor(service: Service | undefined = undefined) {
        super();
        if (service == undefined) return
        this.fromBaseResource(service)
        this.funId = service.funId
        this.inputParamProxies = service.inputParamProxies
        this.outputParamProxies = service.outputParamProxies
    }
}

export class ResourceDeleteForm {
    productId!: number
    resourceType!: ResourceType
    identifier!: string

    constructor(productId: number, resource: Resource) {
        this.productId = productId;
        this.resourceType = resource.resourceType;
        this.identifier = resource.identifier;
    }
}
